import { Vector2, Vector2Tuple, Vector3 } from 'three';

import { moveBoxInNavMesh } from '@spazious/dollhouse-engine';
import { movePolygonInNavMesh, NavMeshQuery } from '@spazious/dollhouse-navmesh';
import { vectorsInSameDirection } from '@spazious/dollhouse-three';

export type DragNode = {
  center: Vector2;
  size: Vector2;
  theta: number;
  outline2D?: Vector2Tuple[];
};

export const moveNodesInNavMesh = (
  nodes: DragNode[],
  movement: Vector3,
  navMeshQuery: NavMeshQuery
) => {
  // for each node, check the possible movement
  // select the minimum movement possible in x, z directions
  // and apply same movement to all nodes
  const currentMovement = movement.clone();
  const multiNodeMovement = nodes.length > 1;
  let iteration = multiNodeMovement ? nodes.length + 1 : 1;
  let success = false;
  while (iteration-- > 0 && !success) {
    success = true;
    for (let i = 0; i < nodes.length; i++) {
      const dragNode = nodes[i];
      const newMovement = dragNode.outline2D
        ? movePolygonInNavMesh(dragNode.outline2D, currentMovement, navMeshQuery!).movement
        : moveBoxInNavMesh(
            dragNode.center.toArray(),
            dragNode.size.toArray(),
            dragNode.theta,
            currentMovement,
            navMeshQuery!
          ).movement;

      if (multiNodeMovement) {
        // check if movement is against the requested direction.
        // If so, do not make any movement
        // else, chose the movement with least distance
        const isMovingBackwards =
          currentMovement.x * newMovement.x < 0 || currentMovement.z * newMovement.z < 0;
        if (isMovingBackwards) {
          success = false;
        } else {
          success = vectorsInSameDirection(currentMovement, newMovement);
          currentMovement.copy(newMovement);
        }
      } else {
        // single node movement, use the result as it is
        currentMovement.copy(newMovement);
      }
    }
  }
  const selectedMovement = success ? currentMovement : currentMovement.setScalar(0);

  return { success, selectedMovement };
};
